import React from 'react';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import { Typography, Box, Stack, CircularProgress } from '@mui/material';
import { useImgSrc } from '../../../hooks/uploads/useImgSrc';
import { usePetFiles } from '../../../hooks/uploads/usePetFiles';
import PersonIcon from '@mui/icons-material/Person'; 
import { getYearsMonthsDiff } from '../../../utils/dateDiff';
import { timeAgo } from '../../../utils/timeAgo';
import PetView from '../../patients/modals/PetView';
import PetForm from '../../patients/forms/PetForm';
import { getPetById } from '../../../api/petService';
import CallSmsButtonCombo from '../../common/controls/CallSMSButtonCombo';
import CopyButton from '../../common/controls/CopyButton';

export default function PatientCard({ pet = null }) {

    const sixMonthsAgo = new Date(); sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    const [imgSrc, setImgSrc] = React.useState(null);
    const [imgData, setImgData] = React.useState(null);

    const [trigger, setTrigger] = React.useState(false);
        // Toggle function
    const toggleTrigger = () => {
        setTrigger(prev => !prev);
    };

    const { petFiles, loading } = usePetFiles(pet.id, {
        page: 0,
        limit: 0,
        tags: "profilePicture",
        trigger
    });

    const { imgLoading, src, error } = useImgSrc(imgSrc);

    const [selectedPet, setSelectedPet] = React.useState(pet);
    const [viewOpen, setViewOpen] = React.useState(false);
    const [petFormOpen, setPetFormOpen] = React.useState(false);
    const [petFormMode, setPetFormMode] = React.useState("edit");

    const handleListItemClick = () => {
        setSelectedPet(selectedPet);
        setViewOpen(true);
    };

    const handleEditPet = (pet) => {
        setSelectedPet(pet)
        setPetFormMode("edit");
        setPetFormOpen(true);
    };

    const onSavePetSuccess = async () => {
        toggleTrigger();
        const updatedPet = await getPetById(selectedPet.id);
        setSelectedPet(updatedPet);
    };

    React.useEffect(() => {
        if (petFiles && petFiles.data && petFiles.data.length > 0)
        {
            setImgSrc(petFiles.data[0].id);
            setImgData(petFiles.data[0]);
        }
    }, [loading]);

    return (
        <>
        <Card
            sx={{ 
                width: "100%",
                maxWidth: 500, 
                boxShadow: 8, 
                transition: '0.3s',
                '&:hover': { boxShadow: 6 },
                borderRadius: 2,
            }}
        >
            {imgSrc ? 
            <Box sx={{ position: 'relative' }}>
                <CardMedia
                    sx={{ height: 300 }}
                    image={src}
                >
                  {imgLoading ?
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300, // or any fixed height
                    }}
                  >
                    <CircularProgress />
                  </Stack>
                  : null}
                </CardMedia>
                <Typography
                    variant="caption"
                    sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: 8,
                        bgcolor: new Date(imgData.createdAt) < sixMonthsAgo ? 'rgba(0,0,0,0.9)' : 'rgba(0,0,0,0.3)',
                        color: new Date(imgData.createdAt) < sixMonthsAgo ? 'red' : 'white',
                        px: 1,
                        borderRadius: 1,
                    }}
                >
                    {`photo uploaded ${timeAgo(imgData.createdAt)}`}
                </Typography>
            </Box>

            : null }
            <CardContent>
                <Typography gutterBottom variant="h5" component="div">
                {selectedPet.name}
                </Typography>
                {selectedPet.deceased ?
                <Typography color="error" variant="body2" sx={{ mt: 0 }}>+deceased</Typography>
                : null}
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {`${selectedPet.gender} ${selectedPet.breed} ${selectedPet.species ? selectedPet.species : selectedPet.speciesLookup?.label}`}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {selectedPet.birthdate ? getYearsMonthsDiff(selectedPet.birthdate) : ""}{selectedPet.birthdate ? " | " : ""}{`${selectedPet?.birthdate || ""}`}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                {selectedPet.neutered ? "neutered " : "not neutered"}{selectedPet.neutered && selectedPet.neutered_date ? selectedPet.neutered_date : ""}
                </Typography>
                <br />
                <Stack direction="row" spacing={1} alignItems="center">
                    <PersonIcon fontSize='small' />
                    <Typography variant="h6">Owner</Typography>
                </Stack>
              
                <Typography variant="body1" sx={{ mb: 0, }}>{`${selectedPet.owner.first_name} ${selectedPet.owner.last_name}`}</Typography>        
                
                <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ mt: 0 }}>{selectedPet.owner.contact_number}</Typography>
                    <CopyButton text={selectedPet.owner.contact_number} />
                </Box>
                <CallSmsButtonCombo phoneNumber={selectedPet.owner.contact_number} bodyText={`Hello ${selectedPet.owner.first_name},`} />

                <Box display="flex" alignItems="center">
                    <Typography variant="body1" sx={{ mt: 0 }}>{selectedPet.owner.email}</Typography>
                    <CopyButton text={selectedPet.owner.email} />
                </Box>
                
            </CardContent>
            <CardActions>
                <Button size="small" onClick={handleListItemClick}>Details</Button>
            </CardActions>
        </Card>
        <PetView 
            handleEditPet={handleEditPet}
            pet={selectedPet}
            open={viewOpen}
            onClose={() => {
                setViewOpen(false);
                toggleTrigger();
            }}
        />
        <PetForm 
            successCallback={onSavePetSuccess}
            pet={selectedPet}
            mode={petFormMode}
            open={petFormOpen}
            onClose={() => {
                setPetFormOpen(false);
            }}
        />
        </>
    );
}